import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Flex, Text, Card, Button, Badge, Tooltip, Progress } from "@radix-ui/themes";
import { useGetAllBrands, useGetWirelessLAN, useGetActiveIntegrations } from "hooks";
import { useProfileContext, useLocationContext } from "context";
import { LockClosedIcon, InfoCircledIcon } from "@radix-ui/react-icons";

const sortByName = (items = []) => [...items].sort((firstItem, secondItem) =>
  (firstItem?.name || "").localeCompare(secondItem?.name || "", undefined, { sensitivity: "base" })
);

const NetworksSelector = () => {
  const navigate = useNavigate();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const organisationID = userProfile.organisation_id;

  // Check for active integrations
  const { data: activeIntegrationsData } = useGetActiveIntegrations(userProfile.organisation_id);
  const [activeIntegrations, setActiveIntegrations] = useState(false);

  useEffect(() => {
    if (activeIntegrationsData && activeIntegrationsData.data.integrations && activeIntegrationsData.data.integrations.length > 0) {
      // Wi-Fi AP provider integration names to check for
      const wifiProviders = ['unifi', 'cisco', 'tplink', 'cambium', 'bconnect', 'extreme'];
      
      // Check if any integration is a Wi-Fi AP provider
      const hasWifiIntegration = activeIntegrationsData.data.integrations.some(integration => 
        wifiProviders.some(provider => 
          integration.name?.toLowerCase().includes(provider)
        )
      );
      
      setActiveIntegrations(hasWifiIntegration);
    } else {
      setActiveIntegrations(false);
    }
  }, [activeIntegrationsData]);

  const navigateToOrganisationLocations = () => {
    navigate("/organisation/locations");
  };

  const navigateToOrganisationLocationsWithBrandModal = () => {
    navigate("/organisation/locations", {
      state: { openEditBrands: true },
      replace: true,
    });
  };

  // Fetch brands
  const { data: brandsData, isLoading: isLoadingBrands, error: brandsError } = useGetAllBrands(organisationID);

  // Fetch all wireless LAN data
  const { data: wirelessLanData, isLoading: isLoadingWirelessLan } = useGetWirelessLAN(organisationID);

  // State to hold filtered data
  const [brandsWithLocations, setBrandsWithLocations] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [networksMap, setNetworksMap] = useState({});

  // Process brands and locations data
  useEffect(() => {
    if (brandsData && locationsData) {
      // Find the default brand
      const defaultBrandItem = brandsData.find((brand) => brand.is_default);
      
      // Get non-default brands with their associated locations
      const nonDefaultBrands = sortByName(brandsData.filter((brand) => !brand.is_default));

      // Add location counts to each brand
      const enhancedBrands = nonDefaultBrands.map((brand) => {
        const locationsInBrand = locationsData.filter((location) => location.brand_id === brand.id);

        return {
          ...brand,
          locations: locationsInBrand,
          locationCount: locationsInBrand.length,
        };
      });

      setBrandsWithLocations(sortByName(enhancedBrands));

      // Filter locations that belong to the default brand only
      const defaultBrandLocations = sortByName(
        locationsData.filter((location) => location.brand_id === defaultBrandItem?.id)
      );

      setIndependentLocations(defaultBrandLocations);
    }
  }, [brandsData, locationsData]);

  // Process wireless LAN data to map networks to locations and brands
  useEffect(() => {
    if (wirelessLanData?.data) {
      const networksByLocationAndBrand = {};

      // Group networks by location_id and brand_id
      wirelessLanData.data.forEach((network) => {
        // Create key for location
        const locationKey = `location_${network.location_id}`;
        if (!networksByLocationAndBrand[locationKey]) {
          networksByLocationAndBrand[locationKey] = [];
        }
        networksByLocationAndBrand[locationKey].push(network);

        // Create key for brand
        const brandKey = `brand_${network.brand_id}`;
        if (!networksByLocationAndBrand[brandKey]) {
          networksByLocationAndBrand[brandKey] = [];
        }
        networksByLocationAndBrand[brandKey].push(network);
      });

      setNetworksMap(networksByLocationAndBrand);
    }
  }, [wirelessLanData]);

  // Get networks for a specific location or brand
  const getNetworksFor = (type, id) => {
    const key = `${type}_${id}`;
    const networks = networksMap[key] || [];

    // Group by network type
    const guestNetwork = networks.find((n) => n.type.includes("Guest"));
    const workNetwork = networks.find((n) => n.type.includes("Work"));
    const staffNetwork = networks.find((n) => n.type.includes("Staff"));

    return { guestNetwork, workNetwork, staffNetwork };
  };

  // Network Badge component
  const NetworkBadge = ({ network, label, showLock = false }) => {
    const isActive = network?.enabled;
    const networkName = network?.name || "Not set";
    const isNotSet = !network?.name;

    const badges = (
      <Flex align="center" gap="0">
        <Badge size="1" variant="soft" 
          color={isActive ? "blue" : "gray"}
          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
          {showLock && <LockClosedIcon width="12" height="12" />} {label}
        </Badge>
        <Badge size="1" variant="solid"
          color={isActive ? "blue" : "gray"}
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
          {networkName}
        </Badge>
      </Flex>
    );

    return (
      <Flex pt="3">
        {isNotSet ? (
          <Tooltip content="Contact support to configure">
            {badges}
          </Tooltip>
        ) : (
          badges
        )}
      </Flex>
    );
  };

  // Handle brand click
  const handleBrandClick = (brand) => {
    // Find the first location in this brand
    const firstLocation = locationsData?.find((location) => location.brand_id === brand.id);

    navigate(`/networks`, {
      state: {
        isBrand: true,
        brandId: brand.id,
        brandName: brand.name,
        ...(firstLocation && { firstLocationId: firstLocation.id }),
      },
    });
  };

  // Handle independent location click
  const handleLocationClick = (location) => {
    navigate(`/networks`, {
      state: {
        isBrand: false,
        locationName: location.name,
        locationId: location.id,
      },
    });
  };

  // Simple loading state
  if (isLoadingBrands || !locationsData || isLoadingWirelessLan) {
    return (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
    );
  }

  // Simple error state
  if (brandsError) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "400px" }}>
        <Text color="red">Error loading network information</Text>
      </Flex>
    );
  }

  const hasCustomBrands = brandsWithLocations.length > 0;

  return (
    <Flex direction="column" style={{ alignSelf: "stretch", width: "100%", overflow: "auto", flex: "1 0 0", height: "100%" }}>
      {/* Main Content Section */}
      {!activeIntegrations ? (
        <Flex style={{ alignSelf: "stretch", flex: '1 0 0', height: '100%' }}>
          <Flex style={{ alignSelf: "stretch", borderRadius: "var(--radius-4)", border: "1px dashed var(--gray-a6)", 
            flex: '1 0 0' }} height="100%" direction="column" justify="center" align="center" gap="5">
            <Flex direction="column" align="center" gap="2" maxWidth="400px" style={{alignItems: 'center', width: '100%'}}>
              <Text size="5" weight="medium" align="center">No connected Wi-Fi integrations</Text>
              <Text size="2" weight="regular" align="center" color="gray">
                After you connect and set up Wi-Fi integrations, then you'll be able to manage your SSID networks here.
              </Text>
            </Flex>
            <Button size="2" variant="soft" color="gray"
              onClick={() => navigate("/integrations/browse-all")}>
              Browse integrations
            </Button>
          </Flex>
        </Flex>
      ) : (
      <Flex direction="column" gap="5" style={{ width: "100%" }}>
        
        { hasCustomBrands && (
          <Flex direction="column" gap="4">
            {/* Brands header section */}
            <Flex justify="between" align="center">
              <Text size="3" weight="medium">Brands</Text>
              <Button size="2" variant="soft" color="gray" 
                onClick={navigateToOrganisationLocationsWithBrandModal}>
                Edit brands
              </Button>
            </Flex>
            {/* Brands settings section */}
            <Flex direction="column" gap="4">
              {brandsWithLocations.map((brand) => {
                const { guestNetwork, workNetwork, staffNetwork } = getNetworksFor("brand", brand.id);
                return (
                  <Card key={brand.id} asChild>
                    <button
                      onClick={() => handleBrandClick(brand)}
                      style={{ width: "100%", textAlign: "left", height: "100%", padding: 'var(--space-4)' }}>
                      <Flex justify="between" align="start" gap="4">
                        {/* Column 1: Brand name and location count */}
                        <Flex direction="column" style={{ width: "25%", color: 'var(--gray-12)' }} gap="1">
                          <Text weight="medium" size="2">
                            {brand.name}
                          </Text>
                          <Text size="1">
                            {brand.locationCount || 0} {brand.locationCount === 1 ? "location" : "locations"}
                          </Text>
                        </Flex>

                        {/* Column 2: Guest network */}
                        <Flex direction="column" style={{ width: "25%" }}>
                          <NetworkBadge network={guestNetwork} label="Guest" />
                        </Flex>

                        {/* Column 3: Work network */}
                        <Flex direction="column" style={{ width: "25%" }}>
                          <NetworkBadge network={workNetwork} label="Work" showLock={true} />
                        </Flex>

                        {/* Column 4: Staff network */}
                        <Flex direction="column" style={{ width: "25%" }}>
                          <NetworkBadge network={staffNetwork} label="Staff" showLock={true} />
                        </Flex>
                      </Flex>
                    </button>
                  </Card>
                );
              })}
            </Flex>
          </Flex>
          
        )}
        
        { independentLocations.length > 0 && (
          <Flex direction="column" gap="4">
            {/* Locations header */}
            <Flex justify="between" align="center">
              <Flex align="center" gap="2">
                <Text size="3" weight="medium">Locations</Text>
                <Tooltip maxWidth="202px" style={{ textAlign: "center"}}
                  content="Independently managed locations, not assigned to any brand.">
                  <InfoCircledIcon/>
                </Tooltip>
              </Flex>
              <Button size="2" variant="soft" color="gray" 
                onClick={navigateToOrganisationLocations}>
                Edit locations
              </Button>
              </Flex>
              {independentLocations.map((location) => {
              const { guestNetwork, workNetwork, staffNetwork } = getNetworksFor("location", location.id);
              return (
                <Card key={location.id} asChild>
                  <button
                    onClick={() => handleLocationClick(location)}
                    style={{ width: "100%", height: "100%", padding: 'var(--space-4)' }}>
                    <Flex justify="between" align="start" gap="4">
                      {/* Column 1: Location name and address */}
                      <Flex direction="column" style={{ width: "25%" }} gap="1">
                        <Text weight="medium" size="2" style={{ color: 'var(--gray-12)'}}>
                          {location.name}
                        </Text>
                        <Text size="1"
                          style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                          {location.address || "No address"}
                        </Text>
                      </Flex>

                      {/* Column 2: Guest network */}
                      <Flex direction="column" style={{ width: "25%" }}>
                        <NetworkBadge network={guestNetwork} label="Guest" />
                      </Flex>

                      {/* Column 3: Work network */}
                      <Flex direction="column" style={{ width: "25%" }}>
                        <NetworkBadge network={workNetwork} label="Work" showLock={true} />
                      </Flex>

                      {/* Column 4: Staff network */}
                      <Flex direction="column" style={{ width: "25%" }}>
                        <NetworkBadge network={staffNetwork} label="Staff" showLock={true} />
                      </Flex>
                    </Flex>
                  </button>
                </Card>
              );
            })}
          </Flex>
        )}

        { ( !hasCustomBrands && independentLocations.length === 0) ? (
          // No brands and no locations
          <Flex direction="column" gap="5" style={{ width: "100%" }}>
            {/* No brands section */}
            <Flex direction="column" gap="4">
              <Flex style={{ justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
                <Text size="3" weight="medium">Brands</Text>
              </Flex>
              <Flex direction="column" gap="5" style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
                <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                  <Text size="2" weight="medium">No brands</Text>
                  <Text size="1" color="gray" align="center">
                    Create brands to easily manage captive portals and networks across your locations.
                  </Text>
                </Flex>
                <Button size="2" variant="soft" onClick={navigateToOrganisationLocationsWithBrandModal}>
                  Manage brands
                </Button>
              </Flex>
            </Flex>
            {/* No locations section */}
            <Flex direction="column" gap="4">
              <Flex gap="2" style={{ alignItems: 'center', alignSelf: 'stretch', justifyContent: 'flex-start' }}>
                <Text size="3" weight="medium">Locations</Text>
                <Tooltip maxWidth="202px" style={{ textAlign: "center"}}
                  content="Independently managed locations, not assigned to any brand.">
                  <InfoCircledIcon/>
                </Tooltip>
              </Flex>
              <Flex direction="column" gap="5"
                style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                  borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
                <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: '296px' }}>
                  <Text size="2" weight="medium">No independent locations</Text>
                  <Text size="1" color="gray" align="center">
                    Create your first location to start setting up your captive portals and networks.
                  </Text>
                </Flex>
                <Button size="2" variant="soft" onClick={navigateToOrganisationLocations}>
                  Manage locations
                </Button>
              </Flex>
            </Flex>
          </Flex>
        ) : (
          ( hasCustomBrands && independentLocations.length === 0) ? (
            <Flex direction="column" gap="4">
              <Flex gap="2" style={{ alignItems: 'center', alignSelf: 'stretch', justifyContent: 'flex-start' }}>
                <Text size="3" weight="medium">Locations</Text>
                <Tooltip maxWidth="202px" style={{ textAlign: "center"}}
                  content="Independently managed locations, not assigned to any brand.">
                  <InfoCircledIcon/>
                </Tooltip>
              </Flex>
              <Flex direction="column" gap="5"
                style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                  borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
                <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: '296px' }}>
                  <Text size="2" weight="medium">No independent locations</Text>
                  <Text size="1" color="gray" align="center">
                    Create your first location to start setting up your captive portals and networks.
                  </Text>
                </Flex>
                <Button size="2" variant="soft" onClick={navigateToOrganisationLocations}>
                  Manage locations
                </Button>
              </Flex>
            </Flex>
          ) : (
            independentLocations.length > 0 && !hasCustomBrands && (
              <Flex direction="column" gap="4">
              <Flex style={{ justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
                <Text size="3" weight="medium">Brands</Text>
              </Flex>
              <Flex direction="column" gap="5" style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
                <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                  <Text size="2" weight="medium">
                    No brands
                  </Text>
                  <Text size="1" color="gray" align="center">
                    Create brands to easily manage captive portals and networks across your locations.
                  </Text>
                </Flex>
                <Button size="2" variant="soft" onClick={navigateToOrganisationLocationsWithBrandModal}>
                  Manage brands
                </Button>
              </Flex>
            </Flex>
          )
        ))}
      </Flex>
      )}
    </Flex>
  );
};

export default NetworksSelector;
